import DateHelper from '../../Core/helper/DateHelper.js';
import InstancePlugin from '../../Core/mixin/InstancePlugin.js';
import GridFeatureManager from '../../Grid/feature/GridFeatureManager.js';
import AttachToProjectMixin from '../../Scheduler/data/mixin/AttachToProjectMixin.js';
import GanttDataProvider from '../util/chart/GanttDataProvider.js';
import SVGChartProvider from '../util/chart/SVGChartProvider.js';

/**
 * @module Gantt/feature/TimelineChart
 */

/**
 * This feature allows drawing line charts on top of the timeline area. Feature consists of two parts: chart and
 * data providers. Chart provider is responsible for rendering the chart, while data provider is responsible
 * for providing data for the chart. Feature itself manages the interaction between them, and tracks lifecycle events
 * of the Gantt chart.
 *
 * {@inlineexample Gantt/feature/TimelineChart.js}
 *
 * Default implementation uses {@link Gantt.util.chart.SVGChartProvider} for rendering the chart using SVG elements,
 * and {@link Gantt.util.chart.GanttDataProvider} as a data source. Those can be replaced and
 * customized by setting the `chartProviderClass` and `dataProviderClass` configs. Custom implementation should subclass
 * corresponding base classes: {@link Gantt.util.chart.TimelineChartProviderBase},
 * {@link Gantt.util.chart.TimelineChartDataProviderBase} and implement required methods.
 *
 * For more information on how to create custom chart provider, see [this guide](#Gantt/guides/customization/scurve.md).
 *
 * Feature will track timeline element resize and scroll and notify the chart provider about them. Chart element
 * normally would be repositioned vertically to always stay in the view. And horizontally it will be scrollable along
 * with the timeline.
 *
 * Feature will also track project model and time axis view model changes to request data for the chart from the
 * data provider and pass it to the chart provider for rendering.
 *
 * Simple usage example:
 * ```javascript
 * // Simple configuration
 * const gantt = new Gantt({
 *     features : {
 *         timelineChart : true
 *     }
 * });
 * ```
 *
 * Chart and data providers can be configured separately.
 * ```javascript
 * const gantt = new Gantt({
 *     features : {
 *         timelineChart : {
 *             chartConfig : {
 *                 tooltipTemplate : function({ dataset, date }) {
 *                     return `
 *                         <div>${dataset.label}: ${DateHelper.as('d', parseInt(dataset.value))} days</div>
 *                         <div>${DateHelper.format(date, this.gantt.displayDateFormat)}</div>
 *                     `;
 *                 }
 *             },
 *             dataProviderConfig : {
 *                 defaultDataset : 'progress'
 *             }
 *         }
 *     }
 * });
 * ```
 *
 * This feature is **disabled** by default.
 *
 * @extends Core/mixin/InstancePlugin
 * @classtype timelineChart
 * @mixes Scheduler/data/mixin/AttachToProjectMixin
 * @feature
 * @demo Gantt/s-curve
 */
export default class TimelineChart extends InstancePlugin.mixin(AttachToProjectMixin) {
    static $name = 'TimelineChart';

    static configurable = {
        /**
         * Class providing chart rendering functionality.
         *
         * @config {Gantt.util.chart.TimelineChartProviderBase}
         * @default
         * @typings {typeof Gantt.util.chart.TimelineChartProviderBase}
         * @category Chart
         */
        chartProviderClass : SVGChartProvider,

        /**
         * Class providing data for the chart.
         *
         * @config {Gantt.util.chart.TimelineChartDataProviderBase}
         * @default
         * @typings {typeof Gantt.util.chart.TimelineChartDataProviderBase}
         * @category Chart
         */
        dataProviderClass : GanttDataProvider,

        /**
         * Configuration for the chart provider.
         *
         * @config {Object}
         * @category Chart
         */
        chartProvider : {},

        /**
         * Configuration for the data provider.
         *
         * @config {Object}
         * @category Chart
         */
        dataProvider : {},

        /**
         * Datasets to be displayed on the chart. Available datasets should be returned by the
         * {@link Gantt.util.chart.TimelineChartDataProviderBase#property-datasets} accessor of the data provider.
         * Changing this property will trigger chart repaint with the new dataset. Set to empty array to not
         * draw anything.
         *
         * @prp {String[]}
         * @category Chart
         */
        selectedDatasets : undefined
    };

    updateDisabled(disabled, was) {
        super.updateDisabled(disabled, was);

        const me = this;

        if (me.chartProvider) {
            me.chartProvider.disabled = disabled;
        }

        if (disabled) {
            me.detachFromProject();
            me.detachListeners('timelineChartListeners');
        }
        else {
            me.attachToProject(me.client.project);
            me.setupListeners();

            if (!me.isConstructing && !me.chartProvider.dataset) {
                me.refreshDataset();
                me.refreshChartOnViewOrDataChange();
            }
        }
    }

    changeChartProvider(chartProvider, was) {
        was?.destroy();

        const instance = new this.chartProviderClass({
            gantt  : this.client,
            config : {
                disabled : this.disabled,
                ...(chartProvider || {})
            }
        });

        this.relayEvents(instance, ['refresh', 'draw']);

        return instance;
    }

    changeDataProvider(dataProvider, was) {
        this.detachListeners('dataProvider');

        was?.destroy();

        const instance = new this.dataProviderClass({
            gantt  : this.client,
            config : dataProvider
        });

        instance.ion({
            name    : 'dataProvider',
            refresh : this.refreshChartOnViewOrDataChange,
            thisObj : this
        });

        return instance;
    }

    updateSelectedDatasets() {
        if (this.chartProvider) {
            this.refreshChartOnViewOrDataChange();
        }
    }

    doDestroy() {
        this.chartProvider.destroy();
        this.dataProvider.destroy();

        super.doDestroy();
    }

    setupListeners() {
        const
            me        = this,
            { client } = me;

        client.timeAxisViewModel.ion({
            name    : 'timelineChartListeners',
            update  : 'refreshChartOnViewOrDataChange',
            thisObj : me
        });

        client.scrollable.ion({
            name    : 'timelineChartListeners',
            scroll  : 'handleViewScroll',
            thisObj : me
        });
    }

    attachToProject(project) {
        super.attachToProject(project);

        // AttachToProjectMixin is called after construct, there is no way to skip this
        // call based on condition. Therefore, this check is required
        if (!this.disabled) {
            project.ion({
                name      : 'project',
                dataReady : 'refreshDataset',
                thisObj   : this
            });
        }
    }

    //#region Event listeners

    handleViewScroll() {
        this.chartProvider.handleViewScroll(this.client.scrollable.y);
    }

    refreshDataset() {
        this.dataProvider.generateDatasets();

        this.refreshChartOnViewOrDataChange();
    }

    refreshChartOnViewOrDataChange() {
        const
            {
                project,
                timeAxisViewModel,
                timeAxis,
                bodyContainer
            }          = this.client,
            viewConfig = this.lastViewConfig = {
                width         : timeAxisViewModel.totalSize,
                height        : bodyContainer.offsetHeight,
                tickSize      : timeAxisViewModel.tickSize,
                projectStartX : project.startDate
                    ? timeAxisViewModel.getPositionFromDate(
                        // no less than time axis start, no greater than time axis end
                        DateHelper.min(DateHelper.max(project.startDate, timeAxis.startDate), timeAxis.endDate)
                    )
                    : 0,
                projectEndX : project.endDate
                    ? timeAxisViewModel.getPositionFromDate(
                        DateHelper.max(DateHelper.min(project.endDate, timeAxis.endDate), timeAxis.startDate)
                    )
                    : timeAxisViewModel.totalSize
            };

        this.chartProvider.update(viewConfig, this.dataProvider.getDataset(viewConfig, this.selectedDatasets));
    }

    //#endregion

    //#region API

    /**
     * Returns metadata for datasets available in the data provider.
     *
     * @property {DatasetMetaData[]}
     * @readonly
     */
    get datasets() {
        return this.dataProvider.datasets;
    }

    //#endregion
}

GridFeatureManager.registerFeature(TimelineChart, false, 'Gantt');
